<template>
    <div>
        <h1 class="mb-8 font-bold text-3xl">
            <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('events.index')">Events
            </inertia-link>
            <span class="text-indigo-400 font-medium">/</span>
            <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('events.show', [eventInfo.id])">
                {{ eventInfo.name }}
            </inertia-link>
            <span class="text-indigo-400 font-medium">/</span>
            <inertia-link class="text-indigo-400 hover:text-indigo-600"
                          :href="route('attendee-import.index', [eventInfo.id])">Import Attendees
            </inertia-link>
            <span class="text-indigo-400 font-medium">/</span>
            Batch #{{ batch.id }}
        </h1>

        <steps class="mb-6" current-step="autofill" :batch-id="batch.id"></steps>

        <info-box>
            Values added below will be automatically applied to every attendee in the current import batch.
            This can be used to add specific field values to attendees or register a group of attendees for a session.
        </info-box>

        <!-- Autofill Fields -->
        <table-wrapper :headers="['Field', 'Value']" :text-wrap="false" class="mb-6">
            <table-row v-if="form.fields.length === 0">
                <table-cell colspan="3">
                    <div class="mx-auto text-center">
                        To autofill field values for the imported attendees, select a field from the list below and then provide a value.<br />
                        This field value will then be applied to all attendees within this import batch.
                    </div>
                </table-cell>
            </table-row>

            <table-row v-for="(field, idx) in form.fields" :key="idx">
                <table-cell>{{ field.table_field }}</table-cell>
                <table-cell>
                    <field-input class="w-full" v-model="field.value" :table-field="field.table_field"></field-input>
                </table-cell>
                <table-cell>
                    <button type="button" @click="removeField(idx)"
                            class="px-1 py-1 ml-auto rounded bg-red-600 text-white text-sm leading-4 font-semibold whitespace-nowrap hover:bg-red-800 focus:bg-red-800"
                            tabindex="-1">
                        <icon name="close" class="w-3 h-3 fill-white"/>
                    </button>
                </table-cell>
            </table-row>

            <table-row>
                <table-cell colspan="3">
                    <select-input v-model="newField">
                        <optgroup v-for="group in fieldGroups" :label="group.label" :key="group.label">
                            <option v-for="field in group.fields" :key="field" v-text="field"></option>
                        </optgroup>
                    </select-input>

                    <button type="button" @click="addField" :disabled="newField === null"
                            class="btn-indigo ml-auto">
                        Add Field
                    </button>
                </table-cell>
            </table-row>
        </table-wrapper>

        <!-- Autofill Sessions -->
        <table-wrapper :headers="['Session', 'Action', 'Notes']" :text-wrap="false" class="mb-6">
            <table-row v-if="form.sessions.length === 0">
                <table-cell colspan="4">
                    <div class="mx-auto text-center">
                        Imported attendees can be automatically registered into event sessions.<br />
                        Select a session from the list below and provide the appropriate action.
                    </div>
                </table-cell>
            </table-row>


            <table-row v-for="(session, idx) in form.sessions" :key="idx">
                <table-cell>{{ findSession(session.session).name }}</table-cell>
                <table-cell>
                    <select-input v-model="session.action" class="w-full">
                        <option value="register">Register</option>
                        <option value="unregister">Unregister</option>
                    </select-input>
                </table-cell>
                <table-cell>
                    <textarea-input v-model="session.notes" rows="1" class="w-full" :disabled="session.action !== 'register'"></textarea-input>
                </table-cell>
                <table-cell>
                    <button type="button" @click="removeSession(idx)"
                            class="px-1 py-1 ml-auto rounded bg-red-600 text-white text-sm leading-4 font-semibold whitespace-nowrap hover:bg-red-800 focus:bg-red-800"
                            tabindex="-1">
                        <icon name="close" class="w-3 h-3 fill-white"/>
                    </button>
                </table-cell>
            </table-row>

            <table-row>
                <table-cell colspan="4">
                    <select-input v-model.number="newSession">
                        <option v-for="session in eventSessions" :key="session.id" :value="session.id"
                                v-text="session.name"></option>
                    </select-input>

                    <button type="button" @click="addSession" :disabled="newSession === null"
                            class="btn-indigo ml-auto">
                        Add Session
                    </button>
                </table-cell>
            </table-row>
        </table-wrapper>

        <div class="mb-6 flex items-center">
            <button class="btn-red" tabindex="-1" type="button" @click="destroy">Delete Batch</button>
            <button class="btn-indigo ml-auto" type="button" @click="saveAutofill">Continue</button>
        </div>

    </div>
</template>

<script>
import Layout from "@/Shared/Layout"
import Icon from "@/Shared/Icon"
import TableWrapper from "@/Shared/Table/TableWrapper"
import TableRow from "@/Shared/Table/TableRow"
import TableCell from "@/Shared/Table/TableCell"
import TableCellEnd from "@/Shared/Table/TableCellEnd"
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import Steps from "./ImportSteps.vue"
import _ from 'lodash'
import TextareaInput from "@/Shared/TextareaInput.vue";
import FieldInput from "../../Shared/FieldInput.vue";
import InfoBox from "../../Shared/InfoBox.vue";

export default {
    metaInfo: {title: "Autofill"},
    components: {
        InfoBox,
        TextareaInput,
        Icon,
        TableWrapper,
        TableRow,
        TableCell,
        TableCellEnd,
        Steps,
        TextInput,
        SelectInput,
        FieldInput,
    },
    layout: Layout,
    props: {
        batch: Object,
        eventInfo: Object,
        fieldGroups: Array,
        fields: Array,
        eventSessions: Array,
        sessions: Array,
    },
    data() {
        return {
            newField: null,
            newSession: null,
            form: this.$inertia.form({
                fields: [],
                sessions: [],
            }),
        }
    },
    mounted() {
        this.form.fields = _.cloneDeep(this.fields)
        this.form.sessions = _.cloneDeep(this.sessions)
    },
    watch: {},
    methods: {
        findSession(id) {
            return this.eventSessions.find((eventSession) => eventSession.id === id)
        },
        addField() {
            if (this.newField === null) {
                return
            }

            this.form.fields.push({table_field: this.newField, value: null})
            this.newField = null
        },
        removeField(idx) {
            this.form.fields.splice(idx, 1)
        },
        addSession() {
            if (this.newSession === null) {
                return
            }

            this.form.sessions.push({session: this.newSession, action: "register", notes: ""})
            this.newSession = null
        },
        removeSession(idx) {
            this.form.sessions.splice(idx, 1)
        },
        saveAutofill() {
            this.form.put(this.route('attendee-import-autofill.update', [this.batch.id]));
        },
        destroy() {
            if (confirm('Are you sure you want to delete this attendee import batch?')) {
                this.$inertia.delete(this.route('attendee-import.destroy', [this.batch.id]))
            }
        },
    },
}
</script>
